import React, { useCallback, useEffect, useState } from "react";
import { EventTypeResponse } from "../../app/models/responseModels/eventTypeResponse";
import {
  EVENT_TYPE_COLUMN_NAME,
  EVENT_TYPE_FILTER_COLUMN_OPTIONS,
  EVENT_TYPE_LABEL_NAME,
} from "./EventTypeConstants";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import AutoCompleteFilter from "../../components/common/autoCompleteFilter/AutoCompleteFilter";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { ExpandMore } from "@mui/icons-material";

type EventTypeDataFilterProps = {
  eventTypeData: EventTypeResponse[];
  setFilteredData: React.Dispatch<React.SetStateAction<EventTypeResponse[]>>;
  filteredData: EventTypeResponse[];
};

type FilterOptions = {
  columnName: string;
  inputRank: number | null;
  displayColumnName: string;
};

const EventTypeDataFilter = ({
  eventTypeData,
  setFilteredData,
  filteredData,
}: EventTypeDataFilterProps) => {
  // ***** States *****
  const [open, setOpen] = React.useState(false);

  const [inputList, setInputList] = useState<FilterOptions[]>([
    {
      columnName: "",
      inputRank: null,
      displayColumnName: "",
    },
  ]);

  const [descriptionFilter, setDescriptionFilter] = useState<string>("");

  const [nameOptions, setNameOptions] = useState<string[]>([]); //Dropdown Options
  const [nameSelectedOptions, setNameSelectedOptions] = useState<string[]>([]); //Dropdown selected value

  const [categoryOptions, setCategoryOptions] = useState<string[]>([]); //Dropdown Options
  const [categorySelectedOptions, setCategorySelectedOptions] = useState<
    string[]
  >([]); //Dropdown selected value

  const toggle = () => {
    setOpen(!open);
  };

  // ***** Callbacks *****
  const getFilterOptions = useCallback(
    (columnName: string) => {
      if (columnName === EVENT_TYPE_COLUMN_NAME.NAME) {
        return nameOptions;
      }
      if (columnName === EVENT_TYPE_COLUMN_NAME.CATEGORY) {
        return categoryOptions;
      }
      return [];
    },
    [nameOptions, categoryOptions]
  );

  const getSelectedOptions = useCallback(
    (columnName: string) => {
      if (columnName === EVENT_TYPE_COLUMN_NAME.NAME) {
        return nameSelectedOptions;
      }
      if (columnName === EVENT_TYPE_COLUMN_NAME.CATEGORY) {
        return categorySelectedOptions;
      }
      return [];
    },
    [nameSelectedOptions, categorySelectedOptions]
  );

  // ***** UseEffects *****
  useEffect(() => {
    const selectedColumnName = inputList[0].columnName;
    //Name dropdown options
    if (selectedColumnName === EVENT_TYPE_COLUMN_NAME.NAME) {
      setNameOptions(
        eventTypeData
          .map((v) => v.name)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setNameOptions(
        filteredData
          .map((v) => v.name)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //Description dropdown options
    if (selectedColumnName === EVENT_TYPE_COLUMN_NAME.CATEGORY) {
      setCategoryOptions(
        eventTypeData
          .map((v) => v.category)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setCategoryOptions(
        filteredData
          .map((v) => v.category)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
  }, [filteredData, inputList, eventTypeData]);

  useEffect(() => {
    let newData = [...eventTypeData];
    //Name Filter
    if (nameSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return nameSelectedOptions.some((s) => {
          return s === a.name;
        });
      });
    }

    //Parcel Center Filter
    if (categorySelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return categorySelectedOptions.some((s) => {
          return s === a.category;
        });
      });
    }

    //Description Filter
    if (descriptionFilter) {
      newData = newData.filter((x) =>
        x.description.toLowerCase().includes(descriptionFilter.toLowerCase())
      );
    }

    setFilteredData(newData);
  }, [
    eventTypeData,
    setFilteredData,
    nameSelectedOptions,
    categorySelectedOptions,
    descriptionFilter,
  ]);

  // ***** Handlers *****
  const handleDescriptionChange = (e: any) => {
    let description = e.target.value;
    setDescriptionFilter(description);
  };

  const handleListAdd = (index: number) => {
    setInputList([
      ...inputList,
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  const handleRemoveItem = (index: number): void => {
    const newList: Array<FilterOptions> = [...inputList];
    newList.splice(index, 1);
    setInputList(newList);

    const removedColumnName = inputList[index].columnName;
    if (removedColumnName === EVENT_TYPE_COLUMN_NAME.NAME) {
      return setNameSelectedOptions([]);
    }
    if (removedColumnName === EVENT_TYPE_COLUMN_NAME.CATEGORY) {
      return setCategorySelectedOptions([]);
    }
  };

  const handleColumnNameChange = (event: any, index: number) => {
    const { value } = event.target;
    const newInputList = [...inputList];
    const oldColumnName = newInputList[index].columnName;

    if (oldColumnName.length > 0) {
      if (oldColumnName === EVENT_TYPE_COLUMN_NAME.NAME) {
        setNameSelectedOptions([]);
      }
      if (oldColumnName === EVENT_TYPE_COLUMN_NAME.CATEGORY) {
        setCategorySelectedOptions([]);
      }
    }
    if (value === EVENT_TYPE_COLUMN_NAME.NAME) {
      newInputList[index].displayColumnName = EVENT_TYPE_LABEL_NAME.NAME;
    }
    if (value === EVENT_TYPE_COLUMN_NAME.CATEGORY) {
      newInputList[index].displayColumnName = EVENT_TYPE_LABEL_NAME.CATEGORY;
    }
    newInputList[index].columnName = value;
    newInputList[index].inputRank = index;
    setInputList(newInputList);
  };

  const handleReset = (e: any) => {
    setDescriptionFilter("");
    setNameSelectedOptions([]);
    setCategorySelectedOptions([]);
    setInputList([
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  const handleSelectedOption = (
    event: Event,
    columnName: string,
    value: string[]
  ) => {
    if (columnName === EVENT_TYPE_COLUMN_NAME.NAME) {
      return setNameSelectedOptions(value);
    }
    if (columnName === EVENT_TYPE_COLUMN_NAME.CATEGORY) {
      return setCategorySelectedOptions(value);
    }
  };

  return (
    <div>
      <Accordion elevation={5} expanded={open}>
        <AccordionSummary
          onClick={toggle}
          expandIcon={
            <>
              <IconButton onClick={toggle} sx={{ color: "white" }}>
                <ExpandMore sx={{ color: "white" }} />
              </IconButton>
            </>
          }
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            minHeight: "36px",
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },

            "&.Mui-expanded": {
              minHeight: "36px",
            },
            backgroundColor: "primary.main",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              color: "white",
            }}
          >
            <Typography>Search Event Type</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ maxHeight: 440, overflowY: "scroll" }}>
          {/* sx={{ height: 220, overflowY: "scroll" }} */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {inputList.map((input, index) => (
              <Grid container rowSpacing={2} columnSpacing={2} key={index}>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  <TextField
                    select
                    key={"columnName"}
                    margin="normal"
                    id={`columnName${index}`}
                    label="Column Name"
                    name={`columnName${index}`}
                    size="small"
                    fullWidth
                    value={inputList[index].columnName}
                    onChange={(event) => handleColumnNameChange(event, index)}
                  >
                    {EVENT_TYPE_FILTER_COLUMN_OPTIONS.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  <AutoCompleteFilter
                    columnName={inputList[index].columnName}
                    displayColumnName={inputList[index].displayColumnName}
                    options={getFilterOptions(inputList[index].columnName)}
                    value={getSelectedOptions(inputList[index].columnName)}
                    onChange={(event: any, selectedValue: string[]) => {
                      handleSelectedOption(
                        event,
                        inputList[index].columnName,
                        selectedValue
                      );
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={2}
                  md={2}
                  lg={2}
                  sx={{ alignSelf: "center" }}
                >
                  <CustomTooltip arrow title="Add Filter">
                    <span>
                      <IconButton
                        onClick={() => handleListAdd(index + 1)}
                        disabled={inputList.length === 10}
                      >
                        <AddIcon sx={{ color: "primary.main" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                  <CustomTooltip arrow title="Remove Filter">
                    <span>
                      <IconButton
                        onClick={() => handleRemoveItem(index)}
                        disabled={inputList.length === 1}
                      >
                        <RemoveIcon sx={{ color: "warning.dark" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                </Grid>
              </Grid>
            ))}
            {/* Description Filter */}
            <Grid container rowSpacing={2} columnSpacing={2}>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  key={"description"}
                  margin="normal"
                  id="description"
                  label="Description"
                  name="description"
                  size="small"
                  fullWidth
                  value={descriptionFilter}
                  onChange={handleDescriptionChange}
                />
              </Grid>
            </Grid>
          </Box>
          <Grid container spacing={2}>
            <Grid xs={12} item>
              <>
                <Box display={"flex"} justifyContent="end">
                  <Button
                    variant="contained"
                    sx={{ mt: 2 }}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </Box>
              </>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default EventTypeDataFilter;
